import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet';
import {connect} from 'react-redux';
import {compose} from 'redux';
import Portal from '@spothero/ui/portal';
import DOMUtils from '@spothero/utils/dom';
import GTMUtils from 'utils/gtm';
import withConfig from 'config/withConfig';
import SHLoader from 'common/loader/SHLoader';
import {updateSelected, getAirportTravelDistance} from 'spot/spot-actions';
import {fetchRates} from 'checkout/checkout-actions';
import {push} from 'router/router-actions';
import {updateTimes} from 'search-request/search-request-actions';
import SHUtils from 'utils/sh-utils';
import SpotImagesMobile from 'spot-details/details/SpotImagesMobile';
import ShuttleInformation from './shuttle/ShuttleInformation';
import BenefitMessage from './header/BenefitMessage';
import SpotDetailsHeader from './header/SpotDetailsHeader';
import FacilityLocation from './FacilityLocation';
import SafetyAmenities from './SafetyAmenities';
import ThingsYouShouldKnow from '../details/ThingsYouShouldKnow';
import HowToRedeemArrivalDeparture from './HowToRedeemArrivalDeparture';
import FAQPanels from 'spot-details/footer/FAQPanels';
import TrustSealValueProps from 'spot-details/footer/TrustSealValueProps';
import AirportPurchaseInformation from './purchase/AirportPurchaseInformation';
import AirportPurchaseSnapshot from './purchase/AirportPurchaseSnapshot';

class SpotDetailsAirport extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        match: PropTypes.shape({
            params: PropTypes.shape({
                spotId: PropTypes.string,
            }),
        }).isRequired,
        currencyType: PropTypes.string,
        spot: PropTypes.object,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        updateSelectedSpot: PropTypes.func,
        updateSearchTimes: PropTypes.func,
        getSpotToAirportTravelDistance: PropTypes.func,
        fetchAirportRates: PropTypes.func,
        isLoading: PropTypes.bool,
        pushTo: PropTypes.func,
        isAirportRateUnavailable: PropTypes.bool,
    };

    constructor(props) {
        super(props);

        this._userUnsubmitted = false;
        this._spotId = parseInt(props.match.params.spotId, 10);
    }

    componentDidMount() {
        const {starts, ends, fetchAirportRates} = this.props;

        fetchAirportRates({
            spotId: this._spotId,
            starts,
            ends,
            airport: true,
            meta: {
                dateTimeChanged: true,
            },
        });

        this._searchPageContainer = DOMUtils.el('body');
        this._bodyClasses = SHUtils.determineInitialLoadBodyClasses({
            isAirport: true,
            pageType: 'facility',
        });

        DOMUtils.addClass(this._searchPageContainer, this._bodyClasses);

        GTMUtils.push({
            event: 'pageview',
            page: 'spotdetail-airport',
        });

        SHUtils.staticScrollTo();
    }

    componentDidUpdate(prevProps) {
        const {spot, getSpotToAirportTravelDistance} = this.props;
        const {spot: prevSpot} = prevProps;

        if (!prevSpot && spot) {
            getSpotToAirportTravelDistance(spot).catch(error => {
                // silently catch this error to ignore errors being logged
            });
        }
    }

    componentWillUnmount() {
        DOMUtils.removeClass(this._searchPageContainer, this._bodyClasses);
    }

    _onCheckoutClick = () => {
        const {
            spot: {
                selectedRate: {fullUrl},
            },
            pushTo,
        } = this.props;

        pushTo(fullUrl);
    };

    render() {
        const {
            isMobile,
            currencyType,
            spot,
            isMonthly,
            starts,
            ends,
            updateSearchTimes,
            fetchAirportRates,
            isLoading,
            pushTo,
            isAirportRateUnavailable,
            updateSelectedSpot,
        } = this.props;
        let facility = null;
        let selectedRate = null;
        let distanceInMiles = null;
        let comparisons = null;

        if (spot) {
            facility = spot.facility;
            selectedRate = spot.selectedRate;
            distanceInMiles = spot.distanceInMiles;
            comparisons = spot.comparisons;
        }

        if (isLoading || !facility) {
            return <SHLoader className="SpotDetailsAirport-loader" />;
        }

        const {
            airport,
            cancellation_allowed: cancellationAllowed,
            amenities_full: amenities,
            rating_info: ratingInfo,
            images,
            title,
            available_transient_inventory: availableTransientInventory,
        } = facility;
        const {
            details: {code: iataCode},
            shuttle,
            lowest_daily_rate: lowestDailyRate,
        } = airport;

        return (
            <div className="SpotDetailsAirport">
                <Helmet>
                    <meta name="robots" content="noindex, follow" />
                </Helmet>
                <div className="SpotDetailsAirport-main-content">
                    <BenefitMessage
                        comparisons={comparisons}
                        iataCode={iataCode}
                        ratingInfo={ratingInfo}
                    />
                    <SpotImagesMobile
                        className="SpotDetailsAirport-header-carousel"
                        images={images}
                        alt={title}
                        height={335}
                        width={629}
                        showDots={false}
                        showArrows={!isMobile}
                        showPageNumbers
                    />
                    <SpotDetailsHeader
                        facility={facility}
                        comparisons={comparisons}
                    />
                    <ShuttleInformation
                        shuttle={shuttle}
                        facilityTitle={title}
                        iataCode={iataCode}
                    />
                    <FacilityLocation
                        facility={facility}
                        distanceInMiles={distanceInMiles}
                    />
                    <SafetyAmenities amenities={amenities} />
                    <ThingsYouShouldKnow
                        rate={selectedRate}
                        facility={facility}
                        isMonthly={isMonthly}
                        // eslint-disable-next-line no-empty-function
                        onShowRateDetailsClick={() => {}}
                    />
                    <HowToRedeemArrivalDeparture
                        selectedRate={selectedRate}
                        facility={facility}
                    />
                    <FAQPanels cancellationAllowed={cancellationAllowed} />
                    <TrustSealValueProps />
                </div>
                {isMobile ? (
                    <Portal>
                        <AirportPurchaseSnapshot
                            cancellationAllowed={cancellationAllowed}
                            availableTransientInventory={
                                availableTransientInventory
                            }
                            displayPrice={
                                selectedRate
                                    ? selectedRate.display_price
                                    : lowestDailyRate
                            }
                            currencyType={currencyType}
                            checkoutDisabled={!selectedRate}
                            onCheckoutClick={this._onCheckoutClick}
                        />
                    </Portal>
                ) : (
                    <div className="SpotDetailsAirport-checkout">
                        <AirportPurchaseInformation
                            onCheckoutClick={this._onCheckoutClick}
                            facility={facility}
                            selectedRate={selectedRate}
                            starts={starts}
                            ends={ends}
                            availableTransientInventory={
                                availableTransientInventory
                            }
                            fetchRates={fetchAirportRates}
                            updateSearchTimes={updateSearchTimes}
                            isAirportRateUnavailable={isAirportRateUnavailable}
                            isFetchingRates={isLoading}
                            pushTo={pushTo}
                            updateSelectedSpot={updateSelectedSpot}
                            isRedesign
                        />
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            currencyType,
            isFetchingRates,
            isAirportRateUnavailable,
            checkoutError,
        },
        searchRequest: {monthly, airport, starts, ends},
        spot: {selected},
    } = state;

    return {
        isLoading: isFetchingRates,
        checkoutError,
        isMonthly: monthly,
        isAirport: airport,
        starts,
        ends,
        spot: selected,
        currencyType,
        isAirportRateUnavailable,
    };
};

const mapDispatchToProps = {
    updateSelectedSpot: updateSelected,
    fetchAirportRates: fetchRates,
    getSpotToAirportTravelDistance: getAirportTravelDistance,
    updateSearchTimes: updateTimes,
    pushTo: push,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SpotDetailsAirport);
